import React, { useState, useCallback, useMemo } from 'react';
import { ArrowLeft, Activity } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { useListSiteEventsQuery } from '../../redux/api/eventsApi';
import type { SiteEvent } from '../../redux/api/eventsApi';

import DashboardModule from '../dashboard/DashboardModule';
import ModuleGrid from '../dashboard/ModuleGrid';
import { useModuleLayout, type ModuleConfig } from '../dashboard/useModuleLayout';
import AISummaryModule from '../dashboard/modules/AISummaryModule';
import SafetyIssuesModule from '../dashboard/modules/SafetyIssuesModule';
import SiteTimelineModule from '../dashboard/modules/SiteTimelineModule';
import EventsLogModule from '../dashboard/modules/EventsLogModule';
import TasksModule from '../dashboard/modules/TasksModule';
import SiteMapModule from '../dashboard/modules/SiteMapModule';
import SiteImagesModule from '../dashboard/modules/SiteImagesModule';

const DEFAULT_MODULES: ModuleConfig[] = [
  { id: 'ai', title: 'AI Briefing', collapsed: false, order: 0, span: 'half' },
  { id: 'safety', title: 'Safety Issues', collapsed: false, order: 1, span: 'half' },
  { id: 'timeline', title: 'Timeline', collapsed: false, order: 2, span: 'full' },
  { id: 'events', title: 'Events Log', collapsed: false, order: 3, span: 'half' },
  { id: 'map', title: 'Site Map', collapsed: false, order: 4, span: 'half' },
  { id: 'images', title: 'Site Images', collapsed: false, order: 5, span: 'half' },
  { id: 'tasks', title: 'Tasks', collapsed: false, order: 6, span: 'full' },
];

const SiteAnalysisTab: React.FC = () => {
  const currentSite = useAppSelector(state => state.site.currentSite);
  const { modules, moveModule, toggleCollapse } = useModuleLayout('site-analysis', DEFAULT_MODULES);

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const siteId = currentSite?.site_id || currentSite?.id || '';
  const siteName = currentSite?.site_name || currentSite?.name || 'Unknown Site';
  const capacity = currentSite?.site_capacity_mw || currentSite?.capacity_mw || 0;
  const location = currentSite?.location || '';

  // Fetch events for timeline and safety modules
  const { data: eventsData } = useListSiteEventsQuery(
    { siteId, limit: 100 },
    { skip: !siteId }
  );
  const events: SiteEvent[] = useMemo(() => eventsData?.items || [], [eventsData]);

  const handleBackToPortfolio = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'portfoliodashboard' } }));
  }, []);

  const handleOpenTab = useCallback((tabType: string) => {
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType } }));
  }, []);

  const handleEventClick = useCallback((eventId: string) => {
    handleOpenTab('eventstasks');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('navigateEventsTasksTab', { detail: { eventId } }));
    }, 100);
  }, [handleOpenTab]);

  const handleTaskClick = useCallback((taskId: string) => {
    handleOpenTab('eventstasks');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('navigateEventsTasksTab', { detail: { taskId } }));
    }, 100);
  }, [handleOpenTab]);

  const renderModule = useCallback((mod: ModuleConfig, dragHandleProps: Record<string, unknown>) => {
    switch (mod.id) {
      case 'ai':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <AISummaryModule variant="site" siteName={siteName} onAskAI={() => {}} />
          </DashboardModule>
        );
      case 'safety':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <SafetyIssuesModule events={events} onEventClick={handleEventClick} />
          </DashboardModule>
        );
      case 'timeline':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('interactivetimeline')}
            viewAllLabel="Full Timeline"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <SiteTimelineModule events={events} onEventClick={handleEventClick} onOpenTimeline={() => handleOpenTab('interactivetimeline')} />
          </DashboardModule>
        );
      case 'events':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('eventstasks')}
            viewAllLabel="All Events"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <EventsLogModule siteId={siteId} onEventClick={handleEventClick} />
          </DashboardModule>
        );
      case 'map':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('sitemap')}
            viewAllLabel="Full Map"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            {currentSite && (
              <SiteMapModule
                siteId={siteId}
                site={currentSite}
                onOpenNodeView={() => handleOpenTab('networkviewer')}
                onOpenFullMap={() => handleOpenTab('sitemap')}
              />
            )}
          </DashboardModule>
        );
      case 'images':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <SiteImagesModule siteId={siteId} />
          </DashboardModule>
        );
      case 'tasks':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('eventstasks')}
            viewAllLabel="All Tasks"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <TasksModule siteId={siteId} onTaskClick={handleTaskClick} />
          </DashboardModule>
        );
      default:
        return null;
    }
  }, [events, siteId, siteName, currentSite, toggleCollapse, handleOpenTab, handleEventClick]);

  if (!siteId) {
    return (
      <div className="eg-page flex items-center justify-center">
        <div className="eg-empty-state">
          <Activity className="eg-empty-icon" />
          <h3 className="eg-empty-title">No Site Selected</h3>
          <p className="eg-empty-text">Select a site from the Portfolio Dashboard to view its analysis.</p>
          <button onClick={handleBackToPortfolio} className="eg-btn-primary mt-4">
            Go to Portfolio Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="eg-page-shell">
      {/* Header zone */}
      <div className="eg-page-header">
        <div className="eg-page-header-glow" />
        <div className="px-6 pt-5 pb-4">
          <button
            onClick={handleBackToPortfolio}
            className="flex items-center gap-1 text-xs font-medium mb-2 transition-colors hover:opacity-80"
            style={{ color: '#17c480' }}
          >
            <ArrowLeft className="w-3 h-3" />
            Portfolio
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#17c480]" />
                <h1 className="eg-page-header-title">{siteName}</h1>
              </div>
              <p className="eg-page-header-subtitle">
                {capacity > 0 && <>{capacity.toFixed(1)} MW</>}
                {location && <> &middot; {location}</>}
              </p>
            </div>
          </div>
        </div>
        <div className="eg-page-accent-divider" />
      </div>

      {/* Content zone */}
      <div className="eg-page-content">
        <div className="px-6 py-5">
          <ModuleGrid
            modules={modules}
            onDragEnd={moveModule}
            renderModule={renderModule}
          />
        </div>
      </div>
    </div>
  );
};

export default SiteAnalysisTab;
